import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { useEffect, useRef, lazy, Suspense, ComponentType } from 'react';
import { registerLoadedModule } from './lib/module-registry';
import Header from './components/Header';
import FullscriptPromoBanner from './components/FullscriptPromoBanner';
import Footer from './components/Footer';
import { SERVICE_AREAS } from './data/service-areas';
import LiveSiteWidgets from './components/LiveSiteWidgets';
import FloatingReviewWidget from './components/FloatingReviewWidget';
import MobileCTA from './components/MobileCTA';
import ErrorBoundary from './components/ErrorBoundary';
import { installChunkReloadListener, withChunkReload } from './lib/chunk-reload';
import JsonLd from './components/JsonLd';
import { organizationSchema, personSchema, localBusinessSchema, websiteSchema } from './lib/schema';
import { trackAITraffic, AdminRedirect } from '@drzach/website-toolkit';
import { SITE } from './data/site';
import { useDeferredMount } from './lib/useDeferredMount';

// Below-fold, non-critical widgets: code-split out of the main/App chunk and
// mounted only once useDeferredMount flips true (idle or first interaction,
// strictly after hydration — see that hook's comment for why this must never
// be part of the initial render). This keeps their JS off the critical path
// entirely instead of merely deferring execution of an already-downloaded
// chunk. FloatingReviewWidget stays a normal eager import: it shares
// GoogleLogo with GoogleReviewsNotice (already eager on Home), so splitting
// it out would only add a request, not remove any bytes from Home's path.
const ChatbotWidget = lazy(() => import('./components/ChatbotWidget'));
const PerfectStormInvitePopup = lazy(() => import('./components/PerfectStormInvitePopup'));

// ============================================
// DYNAMIC PAGE LOADING
// import.meta.glob gives us a map of all page/component files
// so we can load them by name from SITE.pages config
// ============================================
const pageModules = import.meta.glob('./pages/*.tsx') as Record<
  string,
  () => Promise<{ default: ComponentType }>
>;

// A lazy() wrapper whose module can be resolved BEFORE render. Once
// preload() settles, the component renders synchronously — required both
// for the SSR prerender (renderToString cannot wait for a lazy module) and
// for the hydration boot in main.tsx, where the route module must be ready
// before React touches the DOM so no Suspense boundary suspends and the
// server markup is adopted as-is.
//
// A page module may export a static `preloadData(params, pathname)` that
// resolves the code-split data its first render needs (condition articles);
// preloadRouteModules awaits it so server and client both render the
// resolved state and the hydrated DOM never has to be re-rendered.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;
export type RouteParams = Record<string, string | undefined>;
type LoadedPage = AnyComponent & {
  preloadData?: (params: RouteParams, pathname: string) => Promise<unknown>;
};
type PreloadableComponent = AnyComponent & {
  preload?: () => Promise<void>;
  getLoaded?: () => LoadedPage | null;
};

installChunkReloadListener();

function hydratableLazy(loader: () => Promise<{ default: AnyComponent }>, moduleId: string): PreloadableComponent {
  let Loaded: AnyComponent | null = null;
  // A render-time import that fails because a deploy replaced the chunks
  // reloads the (already pushed) target URL once instead of erroring. preload()
  // keeps the raw loader: a failed background warm must not reload the page.
  const Lazy = lazy(withChunkReload(loader));
  const Wrapper: PreloadableComponent = (props: Record<string, unknown>) => {
    const C = Loaded ?? Lazy;
    return <C {...props} />;
  };
  Wrapper.preload = () => {
    registerLoadedModule(moduleId);
    return loader().then((m) => {
      Loaded = m.default;
    });
  };
  Wrapper.getLoaded = () => Loaded;
  return Wrapper;
}

function lazyPage(name: string) {
  const path = `./pages/${name}.tsx`;
  if (!pageModules[path]) {
    console.warn(`Page module not found: ${path}`);
    return hydratableLazy(() => import('./pages/NotFound'), 'src/pages/NotFound.tsx');
  }
  return hydratableLazy(pageModules[path], `src/pages/${name}.tsx`);
}

// ============================================
// SHARED PAGES (identical across all sites)
// ============================================
const Home = hydratableLazy(() => import('./pages/Home'), 'src/pages/Home.tsx');
const HomeAfterWrap = hydratableLazy(() => import('./pages/Home').then((module) => ({ default: module.HomeAfterWrap })), 'src/pages/Home.tsx');
const AboutUs = hydratableLazy(() => import('./pages/AboutUs'), 'src/pages/AboutUs.tsx');
const Pediatric = hydratableLazy(() => import('./pages/Pediatric'), 'src/pages/Pediatric.tsx');
const Prenatal = hydratableLazy(() => import('./pages/Prenatal'), 'src/pages/Prenatal.tsx');
const Family = hydratableLazy(() => import('./pages/Family'), 'src/pages/Family.tsx');
const NewPatientCenter = hydratableLazy(() => import('./pages/NewPatientCenter'), 'src/pages/NewPatientCenter.tsx');
const NewPatientForms = hydratableLazy(() => import('./pages/NewPatientForms'), 'src/pages/NewPatientForms.tsx');
const ScheduleAppointment = hydratableLazy(() => import('./pages/ScheduleAppointment'), 'src/pages/ScheduleAppointment.tsx');
const EventsWorkshops = hydratableLazy(() => import('./pages/EventsWorkshops'), 'src/pages/EventsWorkshops.tsx');
const Contact = hydratableLazy(() => import('./pages/Contact'), 'src/pages/Contact.tsx');
const Privacy = hydratableLazy(() => import('./pages/Privacy'), 'src/pages/Privacy.tsx');
const TermsOfService = hydratableLazy(() => import('./pages/TermsOfService'), 'src/pages/TermsOfService.tsx');
const Thanks = hydratableLazy(() => import('./pages/Thanks'), 'src/pages/Thanks.tsx');
const NotFound = hydratableLazy(() => import('./pages/NotFound'), 'src/pages/NotFound.tsx');
const RHKNGuide = hydratableLazy(() => import('./pages/RHKNGuide'), 'src/pages/RHKNGuide.tsx');
const ThreeWaysToSleep = hydratableLazy(() => import('./pages/ThreeWaysToSleep'), 'src/pages/ThreeWaysToSleep.tsx');
const ThreeWaysToPoop = hydratableLazy(() => import('./pages/ThreeWaysToPoop'), 'src/pages/ThreeWaysToPoop.tsx');
const FreeGuidesForParents = hydratableLazy(() => import('./pages/FreeGuidesForParents'), 'src/pages/FreeGuidesForParents.tsx');
const TalskyTonal = hydratableLazy(() => import('./pages/TalskyTonal'), 'src/pages/TalskyTonal.tsx');
const AnswerHub = hydratableLazy(() => import('./pages/AnswerHub'), 'src/pages/AnswerHub.tsx');
const ThankYouSubmission = hydratableLazy(() => import('./pages/ThankYouSubmission'), 'src/pages/ThankYouSubmission.tsx');
const OurServices = hydratableLazy(() => import('./pages/OurServices'), 'src/pages/OurServices.tsx');
const GentleChiropractorNoCracking = hydratableLazy(
  () => import('./pages/GentleChiropractorNoCracking'),
  'src/pages/GentleChiropractorNoCracking.tsx',
);
const OurTechniques = hydratableLazy(() => import('./pages/OurTechniques'), 'src/pages/OurTechniques.tsx');
const MeetOurDoctors = hydratableLazy(() => import('./pages/MeetOurDoctors'), 'src/pages/MeetOurDoctors.tsx');
const DrAnnaSaylor = hydratableLazy(() => import('./pages/DrAnnaSaylor'), 'src/pages/DrAnnaSaylor.tsx');
const MassageTherapy = hydratableLazy(() => import('./pages/MassageTherapy'), 'src/pages/MassageTherapy.tsx');
const LaserTherapy = hydratableLazy(() => import('./pages/LaserTherapy'), 'src/pages/LaserTherapy.tsx');
const Events = hydratableLazy(() => import('./pages/Events'), 'src/pages/Events.tsx');
const BringAFriendDay = hydratableLazy(() => import('./pages/BringAFriendDay'), 'src/pages/BringAFriendDay.tsx');
const MeetHeather = hydratableLazy(() => import('./pages/MeetHeather'), 'src/pages/MeetHeather.tsx');
const MeetLouAnn = hydratableLazy(() => import('./pages/MeetLouAnn'), 'src/pages/MeetLouAnn.tsx');
const MeetChristina = hydratableLazy(() => import('./pages/MeetChristina'), 'src/pages/MeetChristina.tsx');
const MeetTricia = hydratableLazy(() => import('./pages/MeetTricia'), 'src/pages/MeetTricia.tsx');
const DrJohnJohr = hydratableLazy(() => import('./pages/DrJohnJohr'), 'src/pages/DrJohnJohr.tsx');
const DrZachConner = hydratableLazy(() => import('./pages/DrZachConner'), 'src/pages/DrZachConner.tsx');
const OfficeTour = hydratableLazy(() => import('./pages/OfficeTour'), 'src/pages/OfficeTour.tsx');
const ThePerfectStorm = hydratableLazy(() => import('./pages/ThePerfectStorm'), 'src/pages/ThePerfectStorm.tsx');
const Webinar = hydratableLazy(() => import('./pages/Webinar'), 'src/pages/Webinar.tsx');
const Newsletter = hydratableLazy(() => import('./pages/Newsletter'), 'src/pages/Newsletter.tsx');
const FAQ = hydratableLazy(() => import('./pages/FAQ'), 'src/pages/FAQ.tsx');
const OrientationVideos = hydratableLazy(() => import('./pages/OrientationVideos'), 'src/pages/OrientationVideos.tsx');
const OurAwards = hydratableLazy(() => import('./pages/OurAwards'), 'src/pages/OurAwards.tsx');
const PatientReviews = hydratableLazy(() => import('./pages/PatientReviews'), 'src/pages/PatientReviews.tsx');
const Reviews = hydratableLazy(() => import('./pages/Reviews'), 'src/pages/Reviews.tsx');
const Webinars = hydratableLazy(() => import('./pages/Webinars'), 'src/pages/Webinars.tsx');
const WeCanHelp = hydratableLazy(() => import('./pages/WeCanHelp'), 'src/pages/WeCanHelp.tsx');
const SkedApp = hydratableLazy(() => import('./pages/SkedApp'), 'src/pages/SkedApp.tsx');
const CommunityOutreach = hydratableLazy(() => import('./pages/CommunityOutreach'), 'src/pages/CommunityOutreach.tsx');
const PartnerWithUs = hydratableLazy(() => import('./pages/PartnerWithUs'), 'src/pages/PartnerWithUs.tsx');
const BookDrZach = hydratableLazy(() => import('./pages/BookDrZach'), 'src/pages/BookDrZach.tsx');
const CommunityTalks = hydratableLazy(() => import('./pages/CommunityTalks'), 'src/pages/CommunityTalks.tsx');
const FamilyFocus = hydratableLazy(() => import('./pages/FamilyFocus'), 'src/pages/FamilyFocus.tsx');
const LivingWellNaturally = hydratableLazy(() => import('./pages/LivingWellNaturally'), 'src/pages/LivingWellNaturally.tsx');
const Eczema = hydratableLazy(() => import('./pages/Eczema'), 'src/pages/Eczema.tsx');
const ExtremitiesAdjusting = hydratableLazy(() => import('./pages/ExtremitiesAdjusting'), 'src/pages/ExtremitiesAdjusting.tsx');
const SciaticaPage = hydratableLazy(() => import('./pages/SciaticaPage'), 'src/pages/SciaticaPage.tsx');
const HelpingGrowingPains = hydratableLazy(() => import('./pages/HelpingGrowingPains'), 'src/pages/HelpingGrowingPains.tsx');
const SciaticaTreatment = hydratableLazy(() => import('./pages/SciaticaTreatment'), 'src/pages/SciaticaTreatment.tsx');
const SensoryProcessingDisorder = hydratableLazy(() => import('./pages/SensoryProcessingDisorder'), 'src/pages/SensoryProcessingDisorder.tsx');
const SupplementsPage = hydratableLazy(() => import('./pages/Supplements'), 'src/pages/Supplements.tsx');
const VideoTestimonials = hydratableLazy(() => import('./pages/VideoTestimonials'), 'src/pages/VideoTestimonials.tsx');
const ChiroLand = hydratableLazy(() => import('./pages/ChiroLand'), 'src/pages/ChiroLand.tsx');
const StandardProcessCode = hydratableLazy(() => import('./pages/StandardProcessCode'), 'src/pages/StandardProcessCode.tsx');
const KidsDay = hydratableLazy(() => import('./pages/KidsDay'), 'src/pages/KidsDay.tsx');
const ProviderMingle = hydratableLazy(() => import('./pages/ProviderMingle'), 'src/pages/ProviderMingle.tsx');
const HelpForPMS = hydratableLazy(() => import('./pages/HelpForPMS'), 'src/pages/HelpForPMS.tsx');
const HelpForSlippedDisc = hydratableLazy(() => import('./pages/HelpForSlippedDisc'), 'src/pages/HelpForSlippedDisc.tsx');
const DowagerHumps = hydratableLazy(() => import('./pages/DowagerHumps'), 'src/pages/DowagerHumps.tsx');
const ServiceAreas = hydratableLazy(() => import('./pages/ServiceAreas'), 'src/pages/ServiceAreas.tsx');
const ServiceAreaPage = hydratableLazy(() => import('./pages/ServiceAreaPage'), 'src/pages/ServiceAreaPage.tsx');
const InfoCopyright = hydratableLazy(() => import('./pages/InfoCopyright'), 'src/pages/InfoCopyright.tsx');
const InfoLegalDisclaimer = hydratableLazy(() => import('./pages/InfoLegalDisclaimer'), 'src/pages/InfoLegalDisclaimer.tsx');
const InfoAccessibility = hydratableLazy(() => import('./pages/InfoAccessibility'), 'src/pages/InfoAccessibility.tsx');
const SitemapPage = hydratableLazy(() => import('./pages/SitemapPage'), 'src/pages/SitemapPage.tsx');
const TransferFromCultivate = hydratableLazy(() => import('./pages/TransferFromCultivate'), 'src/pages/TransferFromCultivate.tsx');
const Blog = hydratableLazy(() => import('./pages/Blog'), 'src/pages/Blog.tsx');
const BlogTopic = hydratableLazy(() => import('./pages/BlogTopic'), 'src/pages/BlogTopic.tsx');
const BlogPost = hydratableLazy(() => import('./pages/BlogPost'), 'src/pages/BlogPost.tsx');
const LiveConditionPage = hydratableLazy(() => import('./pages/LiveConditionPage'), 'src/pages/LiveConditionPage.tsx');
const ConditionPageWrapper = hydratableLazy(() => import('./pages/conditions/ConditionPageWrapper'), 'src/pages/conditions/ConditionPageWrapper.tsx');
const LavenderQR = hydratableLazy(() => import('./pages/LavenderQR'), 'src/pages/LavenderQR.tsx');
const RateExperience = hydratableLazy(() => import('./pages/RateExperience'), 'src/pages/RateExperience.tsx');

// ============================================
// DYNAMIC PAGES (driven by SITE.pages config)
// ============================================
const DoctorPage = lazyPage(SITE.pages.doctor.component);

const techniquePages = SITE.pages.techniques.map((t) => ({
  slug: t.slug,
  Component: lazyPage(t.component),
}));

const siteSpecificPages = (SITE.pages.siteSpecific as readonly { slug: string; component: string }[]).map((p) => ({
  slug: p.slug,
  Component: lazyPage(p.component),
}));

// Layout extras (components rendered in Layout, not routes)
const layoutExtraComponents = {} as Record<string, ComponentType>;
const layoutExtras = SITE.pages.layoutExtras
  .map((name) => layoutExtraComponents[name])
  .filter((Component): Component is ComponentType => Boolean(Component));


// ============================================
// ROUTE TABLE
// The single source for both rendering (Layout) and route-module preloading
// (preloadRouteModules, used by main.tsx before hydrate/remount).
// ============================================
const APP_ROUTES = (
  <>
    {/* Shared routes */}
    <Route path="/" element={<Home />} />
    <Route path="/about-us" element={<AboutUs />} />
    <Route path="/pediatric" element={<Pediatric />} />
    <Route path="/pediatric-chiropractic" element={<Pediatric />} />
    <Route path="/prenatal" element={<Prenatal />} />
    <Route path="/prenatal-chiropractic" element={<Prenatal />} />
    <Route path="/family" element={<Family />} />
    <Route path="/new-patient-center" element={<NewPatientCenter />} />
    <Route path="/new-patient-forms" element={<NewPatientForms />} />
    <Route path="/request-an-appointment" element={<ScheduleAppointment />} />
    <Route path="/schedule-appointment" element={<ScheduleAppointment />} />
    <Route path="/book-appointment" element={<ScheduleAppointment />} />
    <Route path="/events-workshops" element={<EventsWorkshops />} />
    <Route path="/contact-us" element={<Contact />} />
    <Route path="/our-services" element={<OurServices />} />
    <Route path="/gentle-chiropractor-no-cracking" element={<GentleChiropractorNoCracking />} />
    <Route path="/our-techniques" element={<OurTechniques />} />
    <Route path="/meet-the-doctors" element={<MeetOurDoctors />} />
    <Route path="/dr-anna-saylor" element={<DrAnnaSaylor />} />
    <Route path="/dr-john-johr" element={<DrJohnJohr />} />
    <Route path="/dr-zach-conner" element={<DrZachConner />} />
    <Route path="/massage-therapy" element={<MassageTherapy />} />
    <Route path="/laser-therapy" element={<LaserTherapy />} />
    <Route path="/events" element={<Events />} />
    <Route path="/bring-a-friend-day" element={<BringAFriendDay />} />
    <Route path="/bring-a-friend-day/" element={<BringAFriendDay />} />
    <Route path="/meet-heather-massage-therapist" element={<MeetHeather />} />
    <Route path="/meet-lou-ann-office-assistant" element={<MeetLouAnn />} />
    <Route path="/christina" element={<MeetChristina />} />
    <Route path="/tricia" element={<MeetTricia />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/info/copyright" element={<InfoCopyright />} />
    <Route path="/info/legal-disclaimer" element={<InfoLegalDisclaimer />} />
    <Route path="/info/accessibility" element={<InfoAccessibility />} />
    <Route path="/sitemap" element={<SitemapPage />} />
    <Route path="/transfer-patients-from-cultivate" element={<TransferFromCultivate />} />
    <Route path="/thanks" element={<Thanks />} />
    <Route path="/3-ways-to-poop" element={<ThreeWaysToPoop />} />
    <Route path="/rhkn-guide" element={<RHKNGuide />} />
    <Route path="/3-ways-to-sleep" element={<ThreeWaysToSleep />} />
    <Route path="/free-guides-for-parents" element={<FreeGuidesForParents />} />
    <Route path="/tonal-chiropractic" element={<TalskyTonal />} />
    <Route path="/talsky-tonal-chiropractic" element={<Navigate to="/tonal-chiropractic" replace />} />
    <Route path="/answers" element={<AnswerHub />} />
    <Route path="/thank-you-for-your-submission" element={<ThankYouSubmission />} />
    <Route path="/office-tour" element={<OfficeTour />} />
    <Route path="/faq" element={<FAQ />} />
    <Route path="/orientation-videos" element={<OrientationVideos />} />
    <Route path="/our-awards-and-accolades" element={<OurAwards />} />
    <Route path="/patient-reviews" element={<PatientReviews />} />
    <Route path="/reviews" element={<Reviews />} />
    <Route path="/webinars" element={<Webinars />} />
    <Route path="/we-can-help" element={<WeCanHelp />} />
    <Route path="/sked-app" element={<SkedApp />} />
    <Route path="/community-outreach" element={<CommunityOutreach />} />
    <Route path="/partner-with-us" element={<PartnerWithUs />} />
    <Route path="/partner-with-us/" element={<PartnerWithUs />} />
    <Route path="/book-dr-zach" element={<BookDrZach />} />
    <Route path="/book-dr-zach/" element={<BookDrZach />} />
    <Route path="/community-talks" element={<CommunityTalks />} />
    <Route path="/community-talks/" element={<CommunityTalks />} />
    <Route path="/family-focus" element={<FamilyFocus />} />
    <Route path="/living-well-naturally" element={<LivingWellNaturally />} />
    <Route path="/eczema" element={<Eczema />} />
    <Route path="/extremities-adjusting" element={<ExtremitiesAdjusting />} />
    <Route path="/sciatica" element={<SciaticaPage />} />
    <Route path="/sensory-processing-disorder-spd" element={<SensoryProcessingDisorder />} />
    <Route path="/supplements" element={<SupplementsPage />} />
    <Route path="/video-testimonials" element={<VideoTestimonials />} />
    <Route path="/van-every-chiro-land" element={<ChiroLand />} />
    <Route path="/standard-process-code" element={<StandardProcessCode />} />
    <Route path="/kids-day" element={<KidsDay />} />
    {/* City service-area landing pages (flat URLs; params cannot own
        a partial segment, so routes are enumerated statically). */}
    <Route path="/service-areas" element={<ServiceAreas />} />
    {SERVICE_AREAS.map((a) => (
      <Route
        key={a.slug}
        path={`/chiropractor-${a.slug}`}
        element={<ServiceAreaPage citySlug={a.slug} />}
      />
    ))}
    <Route path="/provider-mingle" element={<ProviderMingle />} />
    <Route path="/help-for-pms-royal-oak" element={<HelpForPMS />} />
    <Route path="/help-for-slipped-disc-royal-oak" element={<HelpForSlippedDisc />} />
    <Route path="/chiropractic-care-for-dowager-humps" element={<DowagerHumps />} />
    <Route path="/blog" element={<Blog />} />
    {/* Static paginated archive: /blog/page/2/ ... (prerendered, self-canonical) */}
    <Route path="/blog/page/:pageNumber" element={<Blog />} />
    {/* Interactive condition pages — test pages live at /conditions/:slug.
        Invalid slugs redirect to /we-can-help via ConditionPageWrapper. */}
    <Route path="/conditions/:slug" element={<ConditionPageWrapper />} />
    <Route path="/conditions" element={<Navigate to="/we-can-help" replace />} />

    {/* Live condition pages (scraped from original site) */}
    <Route path="/add-adhd" element={<LiveConditionPage />} />
    <Route path="/allergies" element={<LiveConditionPage />} />
    <Route path="/anxiety" element={<Navigate to="/conditions/anxiety/" replace />} />
    <Route path="/conditions/anxiety-stress" element={<Navigate to="/conditions/anxiety/" replace />} />
    <Route path="/asthma" element={<LiveConditionPage />} />
    <Route path="/back-pain" element={<LiveConditionPage />} />
    <Route path="/bedwetting" element={<LiveConditionPage />} />
    <Route path="/boosting-the-immune-system" element={<LiveConditionPage />} />
    <Route path="/breastfeeding-latch" element={<LiveConditionPage />} />
    <Route path="/carpal-tunnel" element={<LiveConditionPage />} />
    <Route path="/chronic-fatigue" element={<Navigate to="/chronic-fatigue-support" replace />} />
    <Route path="/chronic-fatigue-support" element={<LiveConditionPage />} />
    <Route path="/colic" element={<LiveConditionPage />} />
    <Route path="/constipation" element={<LiveConditionPage />} />
    <Route path="/costochondritis" element={<LiveConditionPage />} />
    <Route path="/cranial-work" element={<LiveConditionPage />} />
    <Route path="/depression" element={<LiveConditionPage />} />
    <Route path="/down-syndrome-and-chiropractic-care" element={<LiveConditionPage />} />
    <Route path="/ear-infections" element={<LiveConditionPage />} />
    <Route path="/conquering-fatigue-with-chiropractic" element={<LiveConditionPage />} />
    <Route path="/fibromyalgia" element={<LiveConditionPage />} />
    <Route path="/gi-issues" element={<LiveConditionPage />} />
    <Route path="/headaches-and-migraines" element={<LiveConditionPage />} />
    <Route path="/help-for-frozen-shoulders" element={<LiveConditionPage />} />
    <Route path="/help-for-torticollis" element={<LiveConditionPage />} />
    <Route path="/helping-growing-pains" element={<HelpingGrowingPains />} />
    <Route path="/better-posture" element={<LiveConditionPage />} />
    <Route path="/sciatica-treatment" element={<SciaticaTreatment />} />
    <Route path="/scoliosis-treatment-in-royal-oak" element={<LiveConditionPage />} />
    <Route path="/sleep-how-chiropractic-can-help" element={<LiveConditionPage />} />
    <Route path="/stress" element={<LiveConditionPage />} />
    <Route path="/teens-and-chiropractic" element={<LiveConditionPage />} />
    <Route path="/text-neck" element={<LiveConditionPage />} />
    <Route path="/tmj" element={<LiveConditionPage />} />

    {/* Dynamic: Doctor page */}
    <Route path={SITE.pages.doctor.slug} element={<DoctorPage />} />

    {/* Dynamic: Technique pages (excluding TalskyTonal which is shared) */}
    {techniquePages
      .filter((t) => t.slug !== '/tonal-chiropractic')
      .map((t) => (
        <Route key={t.slug} path={t.slug} element={<t.Component />} />
      ))}

    {/* Dynamic: Site-specific pages */}
    {siteSpecificPages.map((p) => (
      <Route key={p.slug} path={p.slug} element={<p.Component />} />
    ))}

    {/* Lavender event lead-capture landing page */}
    <Route path="/lavender" element={<LavenderQR />} />
    {/* Post-visit review funnel: 5-star nudges to Google, <5-star private feedback */}
    <Route path="/rate" element={<RateExperience />} />
    {/* Admin: render the normal site; AdminRedirect (in AppShell) injects the
        floating embed panel over it when the path is /admin or ?admin=true. */}
    <Route path="/admin" element={<Home />} />
    {/* Perfect Storm explainer + webinar funnel entry (replaced the 2019 blog post, 2026-09-02) */}
    <Route path="/the-perfect-storm" element={<ThePerfectStorm />} />
    {/* Perfect Storm workshop player (cookie-gated, noindex; stream proxied same-origin by the ve-webinar-proxy Worker) */}
    <Route path="/webinar" element={<Webinar />} />
    {/* Monthly newsletter web edition: first name + email + consent gate, then the issue in an iframe (2026-09-02) */}
    <Route path="/newsletter" element={<Newsletter />} />
    {/* Blog topic pages: every post on a condition with 4+ linked posts (2026-09-28) */}
    <Route path="/blog/topic/:key" element={<BlogTopic />} />
    {/* Blog post catch-all: matches any root-level slug not caught above */}
    <Route path="/:slug" element={<BlogPost />} />
    <Route path="*" element={<NotFound />} />
  </>
);

const ROUTE_OBJECTS = createRoutesFromChildren(APP_ROUTES);

// Standalone pages (Zach 2026-09-02): rendered WITHOUT the site banner, nav,
// footer, widgets or popups. Each draws its own slim bar and footer via
// components/StandaloneShell.tsx.
const STANDALONE_ROUTES = new Set(['/webinar', '/the-perfect-storm', '/newsletter', '/rate']);

function isStandaloneRoute(pathname: string): boolean {
  return STANDALONE_ROUTES.has(pathname.replace(/\/$/, '') || '/');
}

/**
 * Resolve the lazy module(s) the given pathname will render. Returns true
 * when every module for the route is loaded and the route can render
 * synchronously; false when the route has no preloadable element (redirects,
 * inline components) and the caller must fall back to a plain remount.
 */
export async function preloadRouteModules(pathname: string): Promise<boolean> {
  const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const matches = matchRoutes(ROUTE_OBJECTS, normalized);
  if (!matches || matches.length === 0) return false;
  const match = matches[matches.length - 1];
  const element = match.route.element;
  const type =
    element && typeof element === 'object' && 'type' in element
      ? (element.type as PreloadableComponent)
      : null;
  if (!type?.preload) return false;
  const jobs = [type.preload()];
  if (normalized === '/') {
    // Layout renders HomeAfterWrap outside the route table on the homepage.
    if (HomeAfterWrap.preload) jobs.push(HomeAfterWrap.preload());
  }
  await Promise.all(jobs);
  const page = type.getLoaded?.();
  if (page?.preloadData) {
    await page.preloadData(match.params as RouteParams, normalized);
  }
  return true;
}

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    // Full-viewport height so the footer stays below the fold while a route
    // chunk resolves; a shorter placeholder lets the footer paint in-viewport
    // and shift back down when content lands (0.24 CLS on interior pages).
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-primary-dark">
        <svg className="w-12 h-12 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const locationKey = `${pathname}${hash}`;
  // Seeded with the first location so the initial (hydration) mount leaves
  // focus alone: the document's natural tab order must start at the skip
  // link and header. Only a real SPA route change moves focus to #main.
  // (A key comparison, not a boolean, so StrictMode's double effect in dev
  // behaves the same as production.)
  const lastLocationKey = useRef(locationKey);

  useEffect(() => {
    if (lastLocationKey.current === locationKey) return;
    lastLocationKey.current = locationKey;

    let cancelled = false;
    let focusTimer: number | undefined;

    const focusMainContent = (attempt = 0) => {
      if (cancelled) return;

      const mainContent = document.getElementById('main');
      if (mainContent) {
        mainContent.focus({ preventScroll: true });
        return;
      }

      if (attempt < 10) {
        focusTimer = window.setTimeout(() => focusMainContent(attempt + 1), 50);
      }
    };

    if (hash) {
      // Scroll to hash target after a brief delay to allow render
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
    // Move focus to main content for accessibility (SPA navigation)
    focusMainContent();

    return () => {
      cancelled = true;
      if (focusTimer) {
        window.clearTimeout(focusTimer);
      }
    };
  }, [pathname, hash, locationKey]);

  return null;
}

function AITrafficTracker() {
  useEffect(() => {
    trackAITraffic();
  }, []);

  return null;
}

function GlobalSchema() {
  return (
    <>
      <JsonLd data={organizationSchema()} />
      {personSchema().map((schema, i) => (
        <JsonLd key={i} data={schema} />
      ))}
      <JsonLd data={localBusinessSchema()} />
      <JsonLd data={websiteSchema()} />
    </>
  );
}

// Mounts the chat and Perfect Storm popup widgets only after useDeferredMount
// flips true, so their chunks never enter the initial hydration pass (see
// that hook's comment). Suspense here is safe: by the time `ready` is true,
// hydration has already committed, so a suspend is just an ordinary
// client-side loading state, not a hydration mismatch.
function DeferredWidgets() {
  const ready = useDeferredMount();
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ChatbotWidget />
      <PerfectStormInvitePopup />
    </Suspense>
  );
}

function showFullscriptBar(pathname: string) {
  const { showOn, shopPaths } = SITE.promotions.fullscript;
  if (showOn === 'all') return true;
  const path = pathname.endsWith('/') ? pathname : `${pathname}/`;
  return (shopPaths as readonly string[]).includes(path);
}

function Layout() {
  const location = useLocation();
  const isHome = location.pathname === '/';

  // Warm the Book Online chooser once the page is idle, so a header, phone
  // bar or CTA tap renders it at once instead of waiting on its chunk. Full
  // page navigations (blog body links) are covered by the speculation rules
  // in index.html.
  useEffect(() => {
    const warm = () => { void ScheduleAppointment.preload?.(); };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(warm, 2500);
    return () => clearTimeout(id);
  }, []);

  // /the-perfect-storm (workshop landing page), /webinar (player) and
  // /newsletter (gated web edition) are their own pages: no site banner,
  // nav, footer, widgets or invite popup —
  // each draws its own slim bar with a link back to the site.
  if (isStandaloneRoute(location.pathname)) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>{APP_ROUTES}</Routes>
      </Suspense>
    );
  }

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:rounded focus:bg-primary-dark focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>
      {showFullscriptBar(location.pathname) && <FullscriptPromoBanner />}
      <div id="containing_wrap">
        <div id="wrap">
          <Header.Navigation />
          <Suspense fallback={<PageLoader />}>
            <Routes>{APP_ROUTES}</Routes>
          </Suspense>
        </div>
        {isHome && (
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <HomeAfterWrap />
          </Suspense>
        )}
        <Footer.FooterMap showMap={isHome} />
      </div>
      {/* Dynamic: Layout extras (e.g., MergerNotification for Cultivate) */}
      {layoutExtras.map((Extra, i) => (
        <Suspense key={i} fallback={null}>
          <Extra />
        </Suspense>
      ))}
      <LiveSiteWidgets />
      {/* Redundant on /reviews, where its fixed box overlaps the page's own
          rating summary and CTA. */}
      {!location.pathname.startsWith('/reviews') && <FloatingReviewWidget />}
      <MobileCTA />
      <DeferredWidgets />
    </>
  );
}

/**
 * Everything inside the router. The browser app wraps it in BrowserRouter;
 * the SSR prerender (src/entry-server.tsx) wraps it in a StaticRouter so both
 * produce the same tree for a given location.
 */
export function AppShell() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <GlobalSchema />
      <AITrafficTracker />
      <AdminRedirect
        adminProjectId={SITE.deployment.adminProjectId}
        previewHostname={SITE.deployment.previewHostname}
      />
      <Layout />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
